// Texto 27 (B3): Escritório & Clientes › Clientes. A lista vem de "Meus clientes" (a mesma lista, a mesma ficha);
// a pasta de cada cliente (documentos e pedidos) é a do antigo Portal do Cliente, ligada pela ficha (c.cid).
// Semáforo: 🟢 em dia · 🟡 algo vence logo (até 3 dias) · 🔴 pedido atrasado ou guia vencida.
import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { router } from 'expo-router';
import type { Reg } from '../../store/types';
import { useDoc } from '../../store/store';
import { novoReg, salvarReg } from '../../store/modulos';
import { ABAS_TRABALHO } from '../../lib/clientes';
import { soDigitos } from '../../lib/cpf';
import { addDays, brYmd, today } from '../../lib/date';
import { diasEntre } from '../../modulos/util';
import { t } from '../../lib/i18n';
import { Chip, Field } from '../ui';
import { C, alpha } from '../theme';

export type Filtro = 'todos' | 'pendencia' | 'vencendo';
const PORTAL = ABAS_TRABALHO.find(a => a.mod === 'portal')!;

/** A situação da pasta do cliente (cor do semáforo e a linha curta do cartão). */
export function situacaoPasta(pasta: Reg | null, portal: Reg[], hoje = today()) {
  const docs = pasta ? portal.filter(r => r.col === 'documento' && r.pai === pasta.id) : [];
  const pedidos = pasta ? portal.filter(r => r.col === 'pedido' && r.pai === pasta.id) : [];
  const aberto = (d: Reg) => !!d.c.vencimento && d.c.status !== 'Pago';
  const pedAtras = pedidos.filter(p => !p.c.entregue && p.c.prazo && p.c.prazo < hoje);
  const guiaVencida = docs.filter(d => aberto(d) && d.c.vencimento < hoje);
  const ate3 = addDays(hoje, 3), ate7 = addDays(hoje, 7);
  const docVence = docs.filter(d => aberto(d) && d.c.vencimento >= hoje && d.c.vencimento <= ate3).sort((a, b) => a.c.vencimento.localeCompare(b.c.vencimento));
  const pedVence = pedidos.filter(p => !p.c.entregue && p.c.prazo && p.c.prazo >= hoje && p.c.prazo <= ate3);
  const guias7 = docs.filter(d => aberto(d) && d.c.vencimento >= hoje && d.c.vencimento <= ate7).length;
  const nomeDoc = (d: Reg) => String(d.c.titulo || d.c.categoria || t('Documento'));
  const quando = (d: string) => { const n = diasEntre(hoje, d); return n === 0 ? t('vence hoje') : n === 1 ? t('vence amanhã') : t('vence em {n} dias', { n }); };
  let linha = t('Tudo em dia');
  if (pedAtras.length) linha = pedAtras.length === 1 ? t('1 documento pedido atrasado') : t('{n} documentos pedidos atrasados', { n: pedAtras.length });
  else if (guiaVencida.length) linha = t('{doc} venceu em {d}', { doc: nomeDoc(guiaVencida[0]), d: brYmd(guiaVencida[0].c.vencimento).slice(0, 5) });
  else if (docVence.length) linha = `${nomeDoc(docVence[0])} ${quando(docVence[0].c.vencimento)}`;
  else if (pedVence.length) linha = pedVence.length === 1 ? t('1 pedido vence logo') : t('{n} pedidos vencem logo', { n: pedVence.length });
  const cor = pedAtras.length || guiaVencida.length ? '🔴' : docVence.length || pedVence.length ? '🟡' : '🟢';
  return { cor, linha, docs, pedidos, guias7 };
}

/** A pasta do cliente (a ficha do antigo Portal ligada a ele); cria na hora, se ainda não existir. */
export function pastaDe(base: Reg, portal: Reg[]): Reg | null {
  return portal.find(r => r.col === 'cliente' && r.c.cid === base.id) ?? null;
}
export function abrirPasta(base: Reg, portal: Reg[]) {
  let p = pastaDe(base, portal);
  if (!p) {
    const valores = Object.fromEntries(Object.entries(PORTAL.campos).map(([k, f]) => [k, f(base.c) ?? '']));
    p = novoReg('cliente', null, { ...valores, cid: base.id });
    salvarReg('portal', p);
  }
  router.push({ pathname: '/aba/[id]', params: { id: 'portal', item: p.id } } as any);
}

/** Os números do painel (B2): clientes com pendência e guias vencendo nos próximos 7 dias. */
export function useNumerosClientes() {
  const base = (useDoc('clientes')?.regs ?? []).filter(r => r.col === 'cliente');
  const portal = useDoc('portal')?.regs ?? [];
  const sits = base.map(b => situacaoPasta(pastaDe(b, portal), portal));
  return { pendencia: sits.filter(s => s.cor === '🔴').length, guias: sits.reduce((s, x) => s + x.guias7, 0) };
}

const tipoDe = (c: Record<string, any>) => String(c.regime || (c.tipo === 'Empresa' ? t('Empresa') : t('Pessoa física')));

export function ClientesEsc({ cor, filtro, setFiltro }: { cor: string; filtro: Filtro; setFiltro: (f: Filtro) => void }) {
  const base = (useDoc('clientes')?.regs ?? []).filter(r => r.col === 'cliente');
  const portal = useDoc('portal')?.regs ?? [];
  const [busca, setBusca] = useState('');
  const linhas = base.map(b => ({ b, s: situacaoPasta(pastaDe(b, portal), portal) }))
    .sort((x, y) => String(x.b.c.nome ?? '').localeCompare(String(y.b.c.nome ?? '')));
  const q = busca.trim().toLowerCase(), qd = soDigitos(busca);
  const achou = (b: Reg) => !q || String(b.c.nome ?? '').toLowerCase().includes(q) || String(b.c.fantasia ?? '').toLowerCase().includes(q)
    || (!!qd && soDigitos(String(b.c.doc ?? '')).includes(qd));
  const pend = linhas.filter(x => x.s.cor === '🔴'), venc = linhas.filter(x => x.s.cor === '🟡');
  const mostrar = (filtro === 'pendencia' ? pend : filtro === 'vencendo' ? venc : linhas).filter(x => achou(x.b));
  return (
    <View style={{ gap: 10 }}>
      <Field value={busca} onChangeText={setBusca} placeholder={'🔎 ' + t('Procurar por nome, CNPJ ou CPF')} />
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Chip small cor={cor} on={filtro === 'todos'} label={t('Todos ({n})', { n: linhas.length })} onPress={() => setFiltro('todos')} />
        <Chip small cor={cor} on={filtro === 'pendencia'} label={t('Com pendência ({n})', { n: pend.length })} onPress={() => setFiltro('pendencia')} />
        <Chip small cor={cor} on={filtro === 'vencendo'} label={t('Vencendo ({n})', { n: venc.length })} onPress={() => setFiltro('vencendo')} />
      </View>
      {!base.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 8 }}>{t('Nenhum cliente ainda. Cadastre em "Meus clientes": ele aparece aqui na hora.')}</Text>}
      {!!base.length && !mostrar.length && <Text style={{ color: C.sub, textAlign: 'center' }}>{t('Nada encontrado.')}</Text>}
      {mostrar.map(({ b, s }) => (
        <Pressable key={b.id} onPress={() => abrirPasta(b, portal)} accessibilityRole="button"
          style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View>
            <View style={{ width: 42, height: 42, borderRadius: 21, backgroundColor: alpha(cor, 0.2), alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: C.text, fontWeight: '800', fontSize: 17 }}>{(String(b.c.apelido || b.c.nome || '?').trim().charAt(0) || '?').toUpperCase()}</Text>
            </View>
            <Text style={{ position: 'absolute', right: -4, bottom: -4, fontSize: 13 }}>{s.cor}</Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={1}>{String(b.c.nome ?? '')}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{tipoDe(b.c)}</Text>
            <Text style={{ color: s.cor === '🔴' ? C.danger : s.cor === '🟡' ? C.warn : C.ok, fontSize: 12, fontWeight: '700' }} numberOfLines={1}>{s.linha}</Text>
          </View>
          <Text style={{ color: C.mute, fontSize: 20 }}>›</Text>
        </Pressable>
      ))}
      <Pressable onPress={() => router.push({ pathname: '/aba/[id]', params: { id: 'clientes' } } as any)} hitSlop={6}>
        <Text style={{ color: C.mute, fontSize: 12, textAlign: 'center' }}>{t('A lista vem de "Meus clientes".')} <Text style={{ color: cor, fontWeight: '700' }}>{t('Abrir Meus clientes')} ›</Text></Text>
      </Pressable>
      <Text style={{ color: C.mute, fontSize: 11, textAlign: 'center' }}>{t('🟢 em dia · 🟡 algo vence logo · 🔴 pedido atrasado ou guia vencida')}</Text>
    </View>
  );
}
